import Box from '@mui/material/Box';
import type React from 'react';
import { useMemo, useState } from 'react';
import { RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import type { Integration } from '../../../../services/api/integrations.api';
import AlertConfigModal from './AlertConfigModal';
import IntegrationHeader from './IntegrationHeader';
import LastExecutionDates from './LastExecutionDates';
import RecentExecutionsGraph from './RecentExecutionsGraph';
import SuccessRateDisplay from './SuccessRateDisplay';

interface IntegrationCardProps {
  integration: Integration;
  onClick?: () => void;
  businessUnitId?: number | null;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({ integration, onClick, businessUnitId }) => {
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  const { last24HourStats } = integration;

  const stats = useMemo(() => {
    if (businessUnitId && last24HourStats?.statsByBusinessUnit?.[businessUnitId]) {
      return last24HourStats.statsByBusinessUnit[businessUnitId];
    }
    return last24HourStats || null;
  }, [businessUnitId, last24HourStats]);

  const { successRate = null, totalExecutions = 0 } = stats || {};

  // The recent-executions sparkline is fed from the pre-computed lastStatuses sample the
  // backend keeps on the integration, so opening the overview fires no per-card request.
  const { statusesToDisplay, executionsToDisplay } = useMemo(() => {
    const sample = businessUnitId
      ? integration.lastStatuses.filter((s) => s.businessUnitId === businessUnitId)
      : integration.lastStatuses;

    const ordered = [...sample]
      .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())
      .slice(0, 100);

    return {
      statusesToDisplay: ordered.map((entry) => entry.status),
      executionsToDisplay: ordered.map((entry) => ({
        endpoint: entry.endpoint ?? '',
        executionTime: entry.executionTime ?? 0,
        status: entry.status,
        createdAt: entry.createdAt ?? '',
      })),
    };
  }, [integration.lastStatuses, businessUnitId]);

  return (
    <>
      <Box
        sx={{
          mb: 2,
          p: 2.5,
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)' } : {},
        }}
        onClick={onClick}
      >
        <IntegrationHeader
          name={integration.name}
          totalExecutions={totalExecutions}
          successRate={successRate}
          onConfigClick={(e) => {
            e.stopPropagation();
            setAlertModalOpen(true);
          }}
        />

        {successRate !== null && (
          <Box>
            <SuccessRateDisplay successRate={successRate} totalExecutions={totalExecutions} />
            <RecentExecutionsGraph
              statuses={statusesToDisplay}
              executions={executionsToDisplay}
              integrationId={integration.id.toString()}
            />
          </Box>
        )}

        <LastExecutionDates lastSuccessDate={integration.lastSuccessDate} lastErrorDate={integration.lastErrorDate} />
      </Box>

      <AlertConfigModal integration={integration} open={alertModalOpen} onClose={() => setAlertModalOpen(false)} />
    </>
  );
};

export default IntegrationCard;
